// 非ログイン状態の場合 Cookie2Token を呼んでトークン取得してからAPIを呼び出すラッパー
// Cookie2Token を呼んでログイン状態になった場合 UserInfo の登録も実施する
import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "~/types/entity/emerIntwcApiWeb.schema";
import type { AccessTokenWithLoginOption } from "~/types/entity/user";
import { cachingFetch } from "~/utils/requestCache";

// API アクセス失敗時に呼び出し側へ通知する情報
export type ServerErrorInfo = {
  status?: number // HTTP ステータス。ネットワークエラー時は undefined
  body?: string // レスポンス body (調査用に画面へ非表示で埋め込む)
};

export type ServerErrorHandler = (error: ServerErrorInfo) => void;

// createOpenapiFetch のオプション。将来の拡張のため位置引数ではなくオプションオブジェクトで受ける。
export type CreateOpenapiFetchOptions = {
  // 認証系 POST のクライアント側キャッシュをバストするためのトークン
  cacheBustT?: number
  // API 失敗 (4xx/5xx・ネットワークエラー) を呼び出し側へ通知するハンドラ
  onServerError?: ServerErrorHandler
};

// HTTP エラー / ネットワークエラーを検知して onServerError へ通知する openapi-fetch ミドルウェア。
// PWA でオフライン化しているため、API 失敗を 0 件と取り違えないよう "Server Error" 表示につなげる。
const createServerErrorMiddleware = (onServerError: ServerErrorHandler): Middleware => ({
  async onResponse({ response }) {
    // 5xx (サーバーエラー) のみをサーバーエラーとして通知する。
    // 2xx/3xx (204 No Content・304 Not Modified 等の正常応答や、onError が生成する status 0 の
    // 擬似レスポンス) では誤通知しない。
    // 4xx (403 権限不足など) は時間をおいても回復しないため、"時間をおいて再度お試しください" の
    // バナーを出さない (誤った再試行を促さない)。
    if (response.status >= 500) {
      let body = "";
      try {
        body = (await response.clone().text()).slice(0, 8000);
      }
      catch {
        // body の取得失敗は致命的ではないため握りつぶす
      }
      onServerError({ status: response.status, body });
    }
    return undefined; // レスポンス自体は変更しない
  },
  onError({ error }) {
    // ネットワークエラー等で fetch が throw したケース
    onServerError({ status: undefined, body: error instanceof Error ? error.message : String(error) });
    // 検索処理が例外でクラッシュしないよう、失敗レスポンスとして解決させる
    return Response.error();
  },
});

export const createOpenapiFetch = async (fetchOption: AccessTokenWithLoginOption, options?: CreateOpenapiFetchOptions) => {
  const accessToken = await fetchOption.getAccessTokenWithLogin(options?.cacheBustT); // await getAccessTokenWithLogin(fetchOption);
  if (!accessToken) {
    return undefined;
  }
  const client = createClient<paths>({
    fetch: cachingFetch, // 認証系 POST のクライアント側キャッシュ (Documents/Plan/AuthCache)
    headers: {
      "Authorization": `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });
  // onServerError を渡した呼び出し (検索系・初期化データ取得系) のみ API 失敗を検知して
  // グローバルな "Server Error" 表示につなげる。
  if (options?.onServerError) {
    client.use(createServerErrorMiddleware(options.onServerError));
  }
  return client;
};

// Content-Typeを付与しない
export const createOpenapiFetchWithoutContentType = async (fetchOption: AccessTokenWithLoginOption, options?: CreateOpenapiFetchOptions) => {
  const accessToken = await fetchOption.getAccessTokenWithLogin(options?.cacheBustT);
  if (!accessToken) {
    return undefined;
  }
  const client = createClient<paths>({
    fetch: cachingFetch,
    headers: {
      "Authorization": `Bearer ${accessToken}`,
    },
  });
  if (options?.onServerError) {
    client.use(createServerErrorMiddleware(options.onServerError));
  }
  return client;
};
